import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux'; // Import useSelector and useDispatch
import { logout, selectCurrentUser, selectIsAuthenticated } from '../../features/auth/authSlice'; // Import auth selectors and logout action
import { isProfessionalRole } from '../../lib/authPortals';
import Icon from '../AppIcon';
import Button from './Button';
import CurrencySwitcher from './CurrencySwitcher';

const Header = () => {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const userMenuRef = useRef(null);
  const mobileMenuRef = useRef(null);

  const user = useSelector(selectCurrentUser); // Get user from Redux store
  const isAuthenticated = useSelector(selectIsAuthenticated); // Get isAuthenticated from Redux store
  const dispatch = useDispatch(); // Initialize useDispatch

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef?.current && !userMenuRef?.current?.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
      if (mobileMenuRef?.current && !mobileMenuRef?.current?.contains(event.target)) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const handleUserMenuToggle = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleLogout = () => {
    dispatch(logout()); // Dispatch logout action
    setIsUserMenuOpen(false);
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path;

  const Logo = () => (
    <Link to="/" className="flex items-center space-x-2"> {/* Changed to root path */}
      <img  src="/assets/images/homelogo.png" alt="Logo" className="w-18 h-10" />
    </Link>
  );

  const NavigationItems = ({ mobile = false }) => (
    <>
      <Link
        to="/"
        className={`${
          mobile ? 'block px-3 py-2 text-base' : 'px-3 py-2 text-sm'
        } font-medium rounded-md transition-colors duration-150 ${
          isActive('/')
            ? 'text-primary bg-primary/10' :'text-foreground hover:text-primary hover:bg-primary/5'
        }`}
      >
        Home
      </Link>

    
      {/* <Link
        to="/hotels"
        className={`${
          mobile ? 'block px-3 py-2 text-base' : 'px-3 py-2 text-sm'
        } font-medium rounded-md transition-colors duration-150 ${
          isActive('/hotels')
            ? 'text-primary bg-primary/10' :'text-foreground hover:text-primary hover:bg-primary/5'
        }`}
      >
        Hotels
      </Link>
      <Link
        to="/hostels"
        className={`${
          mobile ? 'block px-3 py-2 text-base' : 'px-3 py-2 text-sm'
        } font-medium rounded-md transition-colors duration-150 ${
          isActive('/hostels')
            ? 'text-primary bg-primary/10' :'text-foreground hover:text-primary hover:bg-primary/5'
        }`}
      >
        Hostels
      </Link> */}

      <Link
        to="/property-search-browse"
        className={`${
          mobile ? 'block px-3 py-2 text-base' : 'px-3 py-2 text-sm'
        } font-medium rounded-md transition-colors duration-150 ${
          isActive('/property-search-browse')
            ? 'text-primary bg-primary/10' :'text-foreground hover:text-primary hover:bg-primary/5'
        }`}
      >
        Find Properties
      </Link>
      <Link
        to="/notice-board"
        className={`${
          mobile ? 'block px-3 py-2 text-base' : 'px-3 py-2 text-sm'
        } font-medium rounded-md transition-colors duration-150 ${
          isActive('/notice-board') || location.pathname.startsWith('/notice-board/')
            ? 'text-primary bg-primary/10' :'text-foreground hover:text-primary hover:bg-primary/5'
        }`}
      >
        Notice Board
      </Link>
      {/* <Link
        to="/agent-dashboard"
        className={`${
          mobile ? 'block px-3 py-2 text-base' : 'px-3 py-2 text-sm'
        } font-medium rounded-md transition-colors duration-150 ${
          isActive('/agent-dashboard') || location.pathname.startsWith('/agent-dashboard/')
            ? 'text-secondary bg-secondary/10' :'text-foreground hover:text-secondary hover:bg-secondary/5'
        }`}
      >
        Agent Dashboard
      </Link> */}
      {/* <Link
        to="/pro/login"
        className={`${
          mobile ? 'block px-3 py-2 text-base' : 'px-3 py-2 text-sm'
        } font-medium rounded-md transition-colors duration-150 ${
          isActive('/pro/login') || isActive('/pro/register')
            ? 'text-secondary bg-secondary/10' :'text-foreground hover:text-secondary hover:bg-secondary/5'
        }`}
      >
        Real Estate Agents
      </Link> */}
      <Link
        to="/about"
        className={`${
          mobile ? 'block px-3 py-2 text-base' : 'px-3 py-2 text-sm'
        } font-medium rounded-md transition-colors duration-150 ${
          isActive('/about')
            ? 'text-primary bg-primary/10' :'text-foreground hover:text-primary hover:bg-primary/5'
        }`}
      >
        About Us
      </Link>
    </>
  );

  const UserMenu = () => (
    <div className="relative" ref={userMenuRef}>
      <button
        onClick={handleUserMenuToggle}
        className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium text-foreground hover:text-primary hover:bg-primary/5 transition-colors duration-150"
      >
        <div className="w-8 h-8 bg-secondary rounded-full flex items-center justify-center">
          <Icon name="User" size={16} color="white" />
        </div>
        <span className="hidden md:block">{user?.name || 'Account'}</span>
        <Icon name="ChevronDown" size={16} className={`transition-transform duration-150 ${isUserMenuOpen ? 'rotate-180' : ''}`} />
      </button>

      {isUserMenuOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-card border border-border rounded-md shadow-lg z-50 animate-slide-down">
          <div className="py-1">
            <Link
              to={isProfessionalRole(user?.account_role) ? '/agent-dashboard' : '/user-dashboard'}
              className="block px-4 py-2 text-sm text-foreground hover:bg-muted transition-colors duration-150"
              onClick={() => setIsUserMenuOpen(false)}
            >
              <div className="flex items-center space-x-2">
                <Icon name="LayoutDashboard" size={16} />
                <span>My Dashboard</span>
              </div>
            </Link>
            <Link
              to={
                isProfessionalRole(user?.account_role)
                  ? '/agent-dashboard/profile'
                  : '/user-dashboard/profile'
              }
              className="block px-4 py-2 text-sm text-foreground hover:bg-muted transition-colors duration-150"
              onClick={() => setIsUserMenuOpen(false)}
            >
              <div className="flex items-center space-x-2">
                <Icon name="User" size={16} />
                <span>My Profile</span>
              </div>
            </Link>
            <Link
              to="/saved-properties"
              className="block px-4 py-2 text-sm text-foreground hover:bg-muted transition-colors duration-150"
              onClick={() => setIsUserMenuOpen(false)}
            >
              <div className="flex items-center space-x-2">
                <Icon name="Heart" size={16} />
                <span>Saved Properties</span>
              </div>
            </Link>
            <Link
              to="/notice-board"
              className="block px-4 py-2 text-sm text-foreground hover:bg-muted transition-colors duration-150"
              onClick={() => setIsUserMenuOpen(false)}
            >
              <div className="flex items-center space-x-2">
                <Icon name="ClipboardList" size={16} />
                <span>Notice Board</span>
              </div>
            </Link>
            {isProfessionalRole(user?.account_role) && (
              <>
                <Link
                  to="/agent-dashboard/verification"
                  className="block px-4 py-2 text-sm text-foreground hover:bg-muted transition-colors duration-150"
                  onClick={() => setIsUserMenuOpen(false)}
                >
                  <div className="flex items-center space-x-2">
                    <Icon name="ShieldCheck" size={16} />
                    <span>
                      {user?.verification_status === 'verified' ? 'Verified badge' : 'Get Verified'}
                    </span>
                  </div>
                </Link>
                <Link
                  to="/agent-dashboard"
                  className="block px-4 py-2 text-sm text-foreground hover:bg-muted transition-colors duration-150"
                  onClick={() => setIsUserMenuOpen(false)}
                >
                  <div className="flex items-center space-x-2">
                    <Icon name="Briefcase" size={16} />
                    <span>Agent Dashboard</span>
                  </div>
                </Link>
              </>
            )}
            <hr className="my-1 border-border" />
            <button
              onClick={handleLogout}
              className="block w-full text-left px-4 py-2 text-sm text-foreground hover:bg-muted transition-colors duration-150"
            >
              <div className="flex items-center space-x-2">
                <Icon name="LogOut" size={16} />
                <span>Sign Out</span>
              </div>
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const AuthButtons = () => (
    <div className="flex items-center gap-2">
      {/* Always-visible professional entry — not buried in a hover menu */}
      <Button
        variant="outline"
        size="sm"
        onClick={() => navigate('/pro/login')}
        className="hidden lg:inline-flex border-secondary/40 text-secondary hover:bg-secondary/10 hover:text-secondary"
        iconName="Briefcase"
        iconPosition="left"
      >
        Real Estate Agents
      </Button>
      <div className="relative group">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/login')}
          className="text-foreground hover:text-primary"
        >
          User Login
        </Button>
        <div className="absolute right-0 mt-1 w-56 bg-card border border-border rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 py-1">
          <p className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Choose account type
          </p>
          <Link
            to="/login"
            className="block px-4 py-2.5 text-sm text-foreground hover:bg-muted"
          >
            <span className="font-medium">User</span>
            <span className="block text-xs text-muted-foreground">Looking for property</span>
          </Link>
          <Link
            to="/pro/login"
            className="block px-4 py-2.5 text-sm text-foreground hover:bg-muted border-t border-border"
          >
            <span className="font-medium">Real Estate Agents</span>
            <span className="block text-xs text-muted-foreground">Agent · Broker · Advisor login</span>
          </Link>
        </div>
      </div>
      <div className="relative group">
        <Button
          variant="default"
          size="sm"
          onClick={() => navigate('/register')}
        >
          Sign Up
        </Button>
        <div className="absolute right-0 mt-1 w-56 bg-card border border-border rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 py-1">
          <p className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Create account
          </p>
          <Link
            to="/register"
            className="block px-4 py-2.5 text-sm text-foreground hover:bg-muted"
          >
            <span className="font-medium">User signup</span>
            <span className="block text-xs text-muted-foreground">Buy, rent, or invest</span>
          </Link>
          <Link
            to="/pro/register"
            className="block px-4 py-2.5 text-sm text-foreground hover:bg-muted border-t border-border"
          >
            <span className="font-medium">Real Estate Agent signup</span>
            <span className="block text-xs text-muted-foreground">Agent · Broker · Advisor</span>
          </Link>
        </div>
      </div>
    </div>
  );

  return (
    <header className="fixed top-0 left-0 right-0 z-40 bg-background/95 backdrop-blur-nav border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Logo />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1">
            <NavigationItems />
          </nav>

          {/* Desktop User Menu / Auth Buttons */}
          <div className="hidden md:flex items-center gap-2">
            <CurrencySwitcher compact />
            {isAuthenticated ? <UserMenu /> : <AuthButtons />}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button
              onClick={handleMobileMenuToggle}
              className="p-2 rounded-md text-foreground hover:text-primary hover:bg-primary/5 transition-colors duration-150"
            >
              <Icon name={isMobileMenuOpen ? "X" : "Menu"} size={24} />
            </button>
          </div>
        </div>
      </div>
      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-background border-b border-border animate-slide-down" ref={mobileMenuRef}>
          <div className="px-4 pt-2 pb-3 space-y-1">
            <NavigationItems mobile />

            <div className="px-3 py-2 flex items-center justify-between border-t border-border mt-2 pt-3">
              <span className="text-sm font-medium text-foreground">Currency</span>
              <CurrencySwitcher compact />
            </div>
            
            {/* Mobile Auth Section */}
            <div className="pt-2 border-t border-border">
              {isAuthenticated ? (
                <div className="space-y-1">
                  <div className="flex items-center space-x-3 px-3 py-2">
                    <div className="w-8 h-8 bg-secondary rounded-full flex items-center justify-center">
                      <Icon name="User" size={16} color="white" />
                    </div>
                    <span className="text-sm font-medium text-foreground">{user?.name}</span>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="block w-full text-left px-3 py-2 text-base font-medium text-foreground hover:text-primary hover:bg-primary/5 rounded-md transition-colors duration-150"
                  >
                    <div className="flex items-center space-x-2">
                      <Icon name="LogOut" size={16} />
                      <span>Sign Out</span>
                    </div>
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="px-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Users</p>
                  <Button
                    variant="ghost"
                    fullWidth
                    onClick={() => navigate('/login')}
                    className="justify-start text-foreground hover:text-primary"
                  >
                    User login
                  </Button>
                  <Button
                    variant="default"
                    fullWidth
                    onClick={() => navigate('/register')}
                    className="justify-start"
                  >
                    User signup
                  </Button>
                  <p className="px-3 pt-2 text-xs font-medium text-muted-foreground uppercase tracking-wide">Real Estate Agents</p>
                  <Button
                    variant="ghost"
                    fullWidth
                    onClick={() => navigate('/pro/login')}
                    className="justify-start text-foreground hover:text-primary"
                  >
                    Real Estate Agent login
                  </Button>
                  <Button
                    variant="outline"
                    fullWidth
                    onClick={() => navigate('/pro/register')}
                    className="justify-start"
                  >
                    Agent / Broker / Advisor signup
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
